import { Button, Header, Subheader, useStyles } from '@archipro-design/aria';
import * as S from './NetworkError.style';
import { useRootData } from '@modules/root/hook/use-root-data';
import React from 'react';
import { useNetwork } from 'ahooks';
import { spendReloadBudget } from '@modules/error/util/reload-budget';

/** sessionStorage key counting network-recovery reloads spent in the current window. */
const RELOAD_BUDGET_KEY = 'archipro:network-error-reload-count';

/**
 * Why automatic recovery stopped. Both outcomes hand the user a manual retry —
 * a server that never answered is the case that needs one most, and it used to
 * give up silently.
 */
type GaveUpReason = 'server-reachable' | 'server-unreachable';

/**
 * How long to wait for the probe before treating it as a failure.
 *
 * A half-up server accepts the connection and never answers, so a `fetch` with
 * no timeout never settles: neither branch runs, `retryCount` never advances,
 * and the bound below bounds nothing. The user waits on "Network is
 * unreachable" indefinitely with no retry offered.
 */
const PROBE_TIMEOUT_MS = 5_000;

const probeServer = (): Promise<Response> => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
    return fetch('/remix-api/dev/null', { signal: abort.signal }).finally(() =>
        clearTimeout(timer),
    );
};

/**
 * Poll the server until it answers, then reload to retry the navigation the
 * aborted fetch lost. `retryCount` bounds the wait for a server that stays
 * unreachable; `spendReloadBudget` bounds the reloads themselves, which is a
 * different failure. A reachable Remix server in front of a broken backend
 * answers this probe immediately and then fails the page again — without the
 * budget that is an unbroken reload loop the user cannot navigate out of.
 *
 * `signal` stops a superseded loop. Without it, the connection dropping and
 * returning mid-retry starts a second loop beside the first, and both spend the
 * reload budget on the one incident.
 *
 * The copy for the `server-reachable` outcome says only that we stopped
 * retrying. Most errors reaching this component are benign cancelled fetches,
 * so blaming the page would be wrong as often as it was right.
 */
const checkNetwork = (
    retryCount: number,
    onGaveUp: (reason: GaveUpReason) => void,
    signal: AbortSignal,
) => {
    if (signal.aborted) {
        return;
    }
    if (retryCount > 5) {
        onGaveUp('server-unreachable');
        return;
    }
    probeServer()
        .then(() => {
            if (signal.aborted) {
                return;
            }
            /**
             * check we can indeed connect to the server
             */
            if (spendReloadBudget(RELOAD_BUDGET_KEY)) {
                window.location.reload();
                return;
            }
            onGaveUp('server-reachable');
        })
        .catch(() => {
            setTimeout(() => {
                checkNetwork(retryCount + 1, onGaveUp, signal);
            }, 1000);
        });
};

const NetworkError = () => {
    const { css } = useStyles();
    // This renders inside the error boundary, which is exactly where the root
    // loader may have been what failed — so there may be no root data at all.
    const requestId = useRootData()?.requestId;
    const { online } = useNetwork();
    const [gaveUp, setGaveUp] = React.useState<GaveUpReason | null>(null);
    React.useEffect(() => {
        if (!online) {
            return;
        }
        // A fresh loop is retrying again, so an earlier give-up no longer holds.
        setGaveUp(null);
        const loop = new AbortController();
        checkNetwork(0, setGaveUp, loop.signal);
        return () => loop.abort();
    }, [online]);

    const serverAnswered = gaveUp === 'server-reachable';

    return (
        <div className={css(S.BaseStyle)}>
            <Header variant="03">
                {serverAnswered
                    ? 'Still having trouble'
                    : 'Network is unreachable'}
            </Header>
            <Subheader>
                {serverAnswered
                    ? 'We have stopped retrying automatically so this page cannot reload in a loop. Try again, or go back and pick a different page.'
                    : 'Having trouble contacting our servers, check your internet connection and try again.'}
            </Subheader>
            {gaveUp && (
                <Button onClick={() => window.location.reload()} color="dark">
                    TRY AGAIN
                </Button>
            )}
            {requestId && <p>{`Request id: ${requestId}`}</p>}
        </div>
    );
};

export default NetworkError;
